import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import Table from '../../../components/Table';
import Skeleton from 'src/components/Skeleton/Skeleton';
import { useBranding } from 'src/branding/BrandingContextProvider';
import { calculateTableCharLimit } from 'src/pages/sharedPageUtils';
import useWindowSize from 'src/hooks/useWindowSize';
import { ITableColumn } from 'src/components/Table/types';
import '../ServiceRequestDashBoardTable.scss';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import { getServiceRequestById } from 'src/services/client';
import { useTablePagination } from 'src/SharedCustomHooks/UseTablePagination/useTablePagination';
import { useCustomTableColumns } from '../CustomHooks/useCustomTableColumns';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { isExternalPartyRole } from 'src/helpers/permissions';
import { ColumnSelectionDialog } from 'src/components/Table/ColumnSelectionDialog';
import Callout from 'src/components/InformationCallout/Callout';
import Empty from 'src/components/Empty';
import NewBadge from 'src/components/NewBadge/NewBadge';

const SKELETON_ROW_COUNT: number = 13;

interface ServiceRequestDashboardTableProps {
  serviceRequests: any;
  selectedServiceRequests: any;
  onSelectServiceRequest: any;
  onSelectGroup: any;
  onDeleteGroup: any;
  onRemoveFromGroup: any;
  onSelectAll: any;
  allSelected: any;
  // Path the SR detail page's back-breadcrumb returns to. Per-page consumers
  // (Property, Suite) pass their own; main SR dashboard omits and gets the
  // global SR list default from the column helper.
  navigateTo?: string;
  // Label shown on the SR detail page's back-breadcrumb. Per-page consumers
  // (Property, Suite, Contact, etc.) pass a contextual label; SR dashboard
  // omits and gets the generic "Service Requests" fallback.
  navLabel?: string;
  loading?: boolean;
  showColumnSelectionDialog?: boolean;
  onCloseColumnSelectionDialog?: () => void;
  // Redux key for remembering the current page. Per-page consumers pass their
  // own scoped key so each list (dashboard, contact, property, suite) keeps its
  // own page; omit to page locally without persistence.
  persistKey?: string;
}

export const ServiceRequestDashboardTable = ({
  serviceRequests,
  selectedServiceRequests,
  onSelectServiceRequest,
  onSelectGroup,
  onDeleteGroup,
  onRemoveFromGroup,
  onSelectAll,
  allSelected,
  navigateTo,
  navLabel,
  loading = false,
  showColumnSelectionDialog = false,
  onCloseColumnSelectionDialog = () => {},
  persistKey,
}: ServiceRequestDashboardTableProps) => {
  const { branding } = useBranding();
  const user = useSelector((state: any) => selectAuthenticatedUser(state));

  // Internal-only columns (internal/external Visibility + Days Open) are hidden from
  // external parties (home owners, realtors, occupants); every internal/staff role sees them.
  const canSeeInternalColumns: boolean = !isExternalPartyRole(user);

  const [expandedGroups, setExpandedGroups] = useState([]);
  const [characterLimit, setCharacterLimit] = useState<number>(80);

  const { itemsPerPage, onItemsPerPageChange } = useTablePagination();

  const tableRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  // const topScrollRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  // const tableScrollWrapperRef: React.RefObject<HTMLDivElement> =
  // useRef<HTMLDivElement>(null);

  const hasTarionNumbers: boolean = useMemo(() => {
    return serviceRequests.some((serviceRequest: any) => {
      if (serviceRequest.isGroup && serviceRequest.children) {
        return serviceRequest.children.some(
          (child: any) => child.tarionItemNumber !== null
        );
      }
      return serviceRequest.tarionItemNumber !== null;
    });
  }, [serviceRequests]);

  const [images, setImages] = useState<string[]>([]);
  const [showLightbox, setShowLightbox] = useState<boolean>(false);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);

  const openLightboxWithAttachments = (attachments: any[]) => {
    setImages(attachments);
    setLightboxIndex(0);
    setShowLightbox(true);
  };

  const getServiceRequestImages = async (serviceRequest: any) => {
    const response: any = await getServiceRequestById(serviceRequest);

    if (response.ok || response.status === 200) {
      const data = await response.json();
      const attachments = JSON.parse(data.attachment || '[]');

      if (attachments.length > 0) {
        openLightboxWithAttachments(attachments);
      }
    }
  };

  const { customColumns, updateCustomColumns, transformedColumns, allColumns } =
    useCustomTableColumns({
      allSelected,
      onSelectAll,
      expandedGroups,
      setExpandedGroups,
      selectedServiceRequests,
      onSelectGroup,
      onDeleteGroup,
      onSelectServiceRequest,
      onRemoveFromGroup,
      branding,
      hasTarionNumbers,
      onClickAttachment: (serviceRequestId: string) => {
        getServiceRequestImages(serviceRequestId);
      },
      navigateTo,
      navLabel,
      canSeeInternalColumns,
    });

  const skeletonColumns: ITableColumn[] = useMemo(
    () =>
      transformedColumns.map((column: ITableColumn) => ({
        ...column,
        cell: () => <Skeleton variant="text" width="70%" />,
      })),
    [transformedColumns]
  );

  const skeletonData: any[] = useMemo(
    () =>
      Array.from({ length: SKELETON_ROW_COUNT }, (_, index: number) => ({
        id: `service-request-skeleton-${index}`,
      })),
    []
  );

  const { windowSize } = useWindowSize();

  // // Todo: Integrate this with the new JSX below later: WEB-1022 https://app.asana.com/1/41213529744312/task/1211787530791625/comment/1212254369654670?focus=true
  // const { showLeftChevron, showRightChevron } = useHorizontalScrollChevrons({
  //   scrollRef: tableScrollWrapperRef,
  //   topScrollRef: topScrollRef,
  //   dependencies: [serviceRequests],
  // });

  useEffect(() => {
    const newLimit: number = calculateTableCharLimit(tableRef);

    if (newLimit !== characterLimit) {
      setCharacterLimit(newLimit);
    }
  }, [windowSize]);

  const hasNoResults: boolean = !loading && (serviceRequests?.length ?? 0) === 0;

  return (
    <div ref={tableRef}>
      <ColumnSelectionDialog
        allColumns={allColumns}
        selectedColumns={customColumns}
        onChange={updateCustomColumns}
        onClose={onCloseColumnSelectionDialog}
        show={showColumnSelectionDialog}
        note={
          <Callout
            variant="info"
            title="Column preferences are shared"
            subtitle="These columns apply to every Service Request table — the main dashboard and the Service Request tabs on Properties, Suites, Contacts and Items."
          >
            <NewBadge spacedRight />
            You can now add the <strong>Ticket #</strong> column to turn each ticket number
            into a clickable link straight to its service request.
          </Callout>
        }
      />
      {hasNoResults ? (
        <Empty
          direction="ttb"
          headline="No service requests found"
          strapline="Try adjusting or clearing your filters."
          image={
            <img
              alt="No service requests"
              src="/assets/empty/service-requests.svg"
            />
          }
        />
      ) : (
        <Table
          expandedGroups={expandedGroups}
          defaultSort={{
            accessor: 'createdDate',
            direction: 'ascending',
          }}
          columns={loading ? skeletonColumns : transformedColumns}
          data={loading ? skeletonData : serviceRequests}
          stickyHeader
          paginate={{
            itemsPerPage,
            position: 'both',
            persistKey,
          }}
          paginationSkeleton={loading}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      )}
      <ReusableLightbox
        isOpen={showLightbox && images.length > 0}
        images={images}
        initialIndex={lightboxIndex}
        onClose={() => {
          setShowLightbox(false);
          setLightboxIndex(0);
          setImages([]);
        }}
        onIndexChange={(index: number) => setLightboxIndex(index)}
      />
    </div>
  );
};
